import React from "react";

/**
 * AttributionLockBadge — two-state pill surfacing whether an
 * attribution_ledger row is locked. Per BDC spec §Attribution
 * Safeguards, attribution locks once an investor completes an
 * investment or a deal enters live fundraising; only AD can unlock.
 *
 * "Locked" is a positive state for BDCs (the commission credit can no
 * longer be reassigned away), so the badge label is "Secured" — the
 * `lockedAt` prop name is preserved to match the DB column.
 *
 * Props:
 *   lockedAt   ISO string or null. Null → "Open" (still editable).
 *   lockedBy   optional admin id/name for the tooltip.
 *   reason     optional reason text for the tooltip.
 *   className  pass-through.
 */
const SECURED_TOOLTIP =
  "Secured — your commission credit on this row is locked in. " +
  "Triggered when the investor invests or the deal goes live. " +
  "Only an admin can change attribution after this point (audited).";

const OPEN_TOOLTIP =
  "Open — attribution still editable. It will auto-secure once the " +
  "investor invests or the deal goes live, locking your commission credit.";

function AttributionLockBadge({ lockedAt, lockedBy, reason, className = "" }) {
  if (!lockedAt) {
    return (
      <span
        className={`attr-lock attr-lock--open ${className}`}
        title={OPEN_TOOLTIP}
        aria-label={OPEN_TOOLTIP}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          borderRadius: 10,
          background: "#eef2ff",
          color: "#4338ca",
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        <span aria-hidden>🔓</span> Open
      </span>
    );
  }
  const detail = [
    `Secured ${String(lockedAt).slice(0, 16).replace("T", " ")} UTC`,
    lockedBy ? `(unlocked by admin #${lockedBy})` : null,
    reason ? `— ${reason}` : null,
  ].filter(Boolean).join(" ");
  const title = `${SECURED_TOOLTIP}\n\n${detail}`;
  return (
    <span
      className={`attr-lock attr-lock--closed ${className}`}
      title={title}
      aria-label={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 10,
        background: "#ecfdf5",
        color: "#065f46",
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      <span aria-hidden>🔒</span> Secured
    </span>
  );
}

export default AttributionLockBadge;
